import React from 'react';


import Button from 'views/site/Button.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * QuizCTA
 *
 * The recurring "build your network" action: a full-width dark gradient band,
 * square edges, tall enough to stop the scroll — the storefront's own
 * treatment. It reads as an action module rather than another content section
 * precisely because it is the only thing on the page built this way.
 *
 * The accent glow is the dealership's own color, resolved for a dark ground
 * so a deep navy brand does not disappear into the gradient.
 *
 * It appears twice on the page — once before the product detail and once
 * after — because people arrive ready to act at different points, and the
 * walkthrough is the one action worth offering either way.
 */

const QuizCTA = ({ dealership, heading, inset, onOpenQuiz, subtext, tone = 'dark' }) => {

    const mobile = Utils.isMobile();

    /*
     * `tone` exists because of where this lands, not because of how it looks.
     *
     * The dark band works everywhere except immediately above the footer, which
     * is also dark: two dark slabs meeting produce a seam with no edge, and the
     * call to action stops reading as its own thing. The light version is the
     * same band, the same glow and the same button, drawn on the page's own
     * ground so the footer below it still arrives as a change.
     */
    const light = tone === 'light';

    /*
     * `inset` is the answer to the same problem the light tone solves, for the
     * pages that want to keep the dark band. Full bleed, a dark band above a
     * dark footer reads as one continuous region and the call to action stops
     * being a thing you can point at — and a hairline between them does not
     * fix it, because the eye is reading regions rather than edges. Inset, the
     * page's own white ground runs around all four sides and the band becomes
     * an object sitting on the page, with the footer clearly beneath it.
     */

    const ink = light ? Theme.colors.text() : Theme.colors.white;
    const subInk = light ? Theme.colors.subText() : 'rgba(255,255,255,0.72)';

    return (
        <section style={{
            backgroundColor: Theme.colors.white,
            padding: inset ? (mobile ? '0 20px 64px' : '0 40px 96px') : 0,
            width: '100%'
        }}>
            {/* Full bleed, unless inset — then a card on the page's own ground */}
            <div style={{
                background: light
                    ? Theme.colors.white
                    : 'linear-gradient(160deg, #24242a 0%, #141417 100%)',
                borderRadius: inset ? 28 : 0,
                borderTop: light ? `1px solid ${Theme.colors.border()}` : 'none',
                margin: inset ? '0 auto' : 0,
                maxWidth: inset ? Theme.layout.maxWidth : undefined,
                boxShadow: light ? 'none' : '0 26px 60px rgba(15, 23, 42, 0.30)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                    minHeight: mobile ? null : 625,
                overflow: 'hidden',
                padding: mobile ? '44px 26px' : '62px 56px',
                position: 'relative',
                textAlign: 'center',
                width: '100%'
            }}>
                {/* Accent glow, sized to the taller band and anchored top-right */}
                <div style={{
                    /* The glow is the dealership's color either way. On white
                       it has to be far weaker or it reads as a print defect. */
                    background: light ? null : `radial-gradient(circle, ${Theme.tint(0.45, Theme.colors.primaryFillOnDark())} 0%, rgba(0,0,0,0) 70%)`,
                    borderRadius: '50%',
                    height: mobile ? 320 : 640,
                    pointerEvents: 'none',
                    position: 'absolute',
                    right: mobile ? -80 : -180,
                    top: mobile ? -120 : -220,
                    width: mobile ? 320 : 640
                }} />

                <div style={{ position: 'relative' }}>
                    <img
                    alt={''}
                    src={'images/graci-ai-icon.png'}
                    style={{
                        borderRadius: 38,
                        boxShadow: light ? '0 10px 24px rgba(0,0,0,0.10)' : '0 10px 24px rgba(255,255,255,0.15)',
                        display: 'inline-flex',
                        height: 76,
                        marginBottom: 20,
                        width: 76
                    }} />

                    <span style={{
                        ...Theme.textStyles.sectionHeading({ color: ink, textAlign: 'center' }),
                        display: 'block',
                        marginBottom: 14
                    }}>{heading || 'Take us for a walk through your home'}</span>

                    <span style={{
                        ...Theme.textStyles.body({ color: subInk, fontSize: 16 }),
                        display: 'block',
                        margin: '0 auto',
                        maxWidth: 725
                    }}>{subtext || `Your house came with smoke detectors and they detect smoke. They stay silent for carbon monoxide from the furnace or a car in the garage, for a fire that starts in the attic or behind the dryer, and for water running behind the dishwasher. Five questions and you'll know which of those your home has no answer for, then ${dealership?.name || 'your local dealer'} can come out and walk it with you.`}</span>

                    <div style={{ marginTop: 30 }}>
                        <Button
                        icon={'stars'}
                        label={"What your detectors don't catch"}
                        onClick={onOpenQuiz}
                        style={{
                            backgroundColor: '#7D365D',
                            color: Theme.colors.white,
                            fontSize: 16,
                            padding: '16px 40px'
                        }}
                        type={'primary'} />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default QuizCTA;
